import { cn } from '@/shared/utils/cn';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

const variantClasses: Record<ButtonVariant, string> = {
  primary: [
    'border border-adipa-primary bg-adipa-primary text-adipa-primary-foreground',
    'enabled:hover:border-adipa-primary-hover enabled:hover:bg-adipa-primary-hover',
    'focus-visible:ring-2 focus-visible:ring-adipa-primary focus-visible:ring-offset-2 focus-visible:ring-offset-adipa-surface',
    'disabled:opacity-50',
  ].join(' '),
  secondary: [
    'border border-adipa-secondary bg-adipa-secondary text-adipa-secondary-foreground',
    'enabled:hover:border-adipa-primary enabled:hover:bg-adipa-surface-celeste-soft',
    'focus-visible:ring-2 focus-visible:ring-adipa-primary focus-visible:ring-offset-2 focus-visible:ring-offset-adipa-surface',
    'disabled:opacity-50',
  ].join(' '),
  outline: [
    'border border-adipa-border-strong bg-adipa-surface text-adipa-primary',
    'enabled:hover:bg-adipa-surface-muted',
    'focus-visible:ring-2 focus-visible:ring-adipa-primary focus-visible:ring-offset-2 focus-visible:ring-offset-adipa-surface',
    'disabled:opacity-50',
  ].join(' '),
  ghost: [
    'border border-transparent bg-transparent text-adipa-primary',
    'enabled:hover:bg-adipa-surface-muted',
    'focus-visible:ring-2 focus-visible:ring-adipa-primary focus-visible:ring-offset-2 focus-visible:ring-offset-adipa-surface',
    'disabled:opacity-50',
  ].join(' '),
  destructive: [
    'border border-adipa-error-border bg-adipa-surface text-adipa-error',
    'enabled:hover:border-adipa-error enabled:hover:bg-adipa-error-surface',
    'focus-visible:ring-2 focus-visible:ring-adipa-error-ring focus-visible:ring-offset-2 focus-visible:ring-offset-adipa-surface',
    'disabled:opacity-50',
  ].join(' '),
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      aria-busy={loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-adipa-md font-medium',
        'enabled:cursor-pointer disabled:cursor-not-allowed',
        'transition-colors duration-adipa-fast outline-none',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {loading && (
        <svg
          className="h-4 w-4 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      {children}
    </button>
  );
}
